import React, { useEffect, useState, useCallback } from 'react'
import { LuDollarSign } from 'react-icons/lu'
import { FiAlertCircle, FiTrash2, FiPlus } from 'react-icons/fi'

import { CustomCurrencyInput, Checkbox } from '@components/common/form'
import { DividerLine } from '@components/common'

import {
  VendorPricingAgreement,
  VendorPricingAgreementTier,
  VendorPricingAgreementServiceTypes,
  ServiceTypeToString,
  VendorPricingAgreementServiceType,
} from '@types'

import { colors } from '@constants'

interface VendorPricingAgreementTiersProps {
  pricingAgreement: VendorPricingAgreement | null
  onChangeActive: (value: boolean) => void
  onChangeServiceTypes: (
    serviceType: VendorPricingAgreementServiceType,
    checked: boolean,
  ) => void
  onChangeTiers: (
    idx: number,
    field: keyof VendorPricingAgreementTier,
  ) => (value: number) => void
  onAddTier: () => void
  onRemoveTier: (index: number) => void
  onSave: () => Promise<void>
  onCancel: () => void
}

const VendorPricingAgreementTiers: React.FC<
  VendorPricingAgreementTiersProps
> = ({
  pricingAgreement,
  onChangeActive,
  onChangeServiceTypes,
  onChangeTiers,
  onAddTier,
  onRemoveTier,
  onSave,
  onCancel,
}) => {
  const [errors, setErrors] = useState<
    Record<string, Record<number, string> | string>
  >({})
  const isValid = Object.keys(errors).length === 0

  const validateTiers = useCallback(() => {
    const newErrors: Record<string, Record<number, string> | string> = {}
    const tiersErrors: Record<number, string> = {}

    if ((pricingAgreement?.serviceTypes || []).length === 0) {
      newErrors.serviceTypes = 'Service types are required'
    }

    const tiers = pricingAgreement?.tiers || []
    tiers.forEach((tier, index) => {
      if (index === 0 && tier.lowerLimit !== 0) {
        tiersErrors[index] = 'Tiers must always start from $0.00'

        return
      }
      if (index !== tiers.length - 1 && tier.upperLimit === null) {
        tiersErrors[index] =
          'Only the last tier can have an unlimited upper limit'

        return
      }
      if (tier.upperLimit && tier.lowerLimit >= tier.upperLimit) {
        tiersErrors[index] = 'Upper limit must be greater than lower limit'

        return
      }
      if (index > 0 && tiers[index - 1].upperLimit !== null) {
        const prevUpperLimit = tiers[index - 1].upperLimit as number
        if (Math.abs(tier.lowerLimit - prevUpperLimit) > 0.01) {
          tiersErrors[index] =
            `Gap detected: should start at $${prevUpperLimit.toFixed(2)}`

          return
        }
      }
    })
    if (Object.keys(tiersErrors).length > 0) {
      newErrors.tiers = tiersErrors
    }

    setErrors(newErrors)

    return Object.keys(newErrors).length === 0
  }, [pricingAgreement?.tiers, pricingAgreement?.serviceTypes])

  useEffect(() => {
    validateTiers()
  }, [validateTiers])

  const renderServiceTypes = () => {
    return Object.values(VendorPricingAgreementServiceTypes).map(
      (serviceType) => {
        const checked = pricingAgreement?.serviceTypes.includes(serviceType)

        return (
          <Checkbox
            key={serviceType}
            checkedColor={colors.green}
            label={ServiceTypeToString(serviceType)}
            checked={checked}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChangeServiceTypes(serviceType, e.target.checked)
            }
          />
        )
      },
    )
  }

  const renderTier = (tier: VendorPricingAgreementTier, index: number) => {
    const error = errors.tiers?.[index]

    return (
      <div
        key={index}
        className="flex flex-col gap-2 w-full rounded-lg p-4"
        style={{ border: `1px solid ${error ? 'red' : colors.gray200}` }}
      >
        <div className="flex flex-row items justify-between items-center">
          <div className="flex flex-row items-center gap-2">
            <p
              className="px-2 py-1 rounded-md"
              style={{ border: `1px solid ${colors.gray200}` }}
            >
              Tier {index + 1}
            </p>
            {error && (
              <p className="bg-red-500 text-white px-2 py-1 rounded-md ml-2 text-center">
                Error
              </p>
            )}
          </div>
          <FiTrash2
            color="red"
            size={20}
            className="cursor-pointer hover:bg-red-200 rounded-md"
            onClick={() => onRemoveTier(index)}
          />
        </div>
        <div className="flex flex-row w-full justify-between">
          <CustomCurrencyInput
            label="Lower Limit"
            onChange={onChangeTiers(index, 'lowerLimit')}
            value={tier.lowerLimit}
            decimals={2}
            step={1}
            min={0}
            prefix="$"
            width="30%"
          />
          <CustomCurrencyInput
            label="Upper Limit"
            onChange={onChangeTiers(index, 'upperLimit')}
            placeholder="Unlimited"
            value={tier.upperLimit || undefined}
            prefix="$"
            step={1}
            decimals={2}
            width="30%"
          />
          <CustomCurrencyInput
            label="Discount %"
            onChange={onChangeTiers(index, 'discountPercentage')}
            value={tier.discountPercentage}
            decimals={2}
            step={0.5}
            min={0}
            max={100}
            suffix="%"
            width="30%"
          />
        </div>
        {error && <ErrorMessage error={error} />}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 w-full border-2 border-gray-300 rounded-md p-4 bg-white justify-center">
      <div className="flex flex-row items-center gap-2">
        <LuDollarSign size={30} />
        <p className="text-lg font-bold">Vendor Volume Discount Tiers</p>
      </div>
      <p className="text-sm text-gray-500">
        Configure volume-based discount tiers. Tiers must be continuous with no
        gaps, starting from $0.
      </p>
      <div className="my-2">
        <Checkbox
          label="Active"
          checkedColor={colors.green}
          checked={pricingAgreement?.active}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChangeActive(e.target.checked)
          }
        />
      </div>
      {Object.keys(errors).length > 0 && (
        <ErrorMessage error="Please fix the validation errors below before saving." />
      )}
      <div className="flex flex-col items-center gap-2">
        {errors.serviceTypes && (
          <ErrorMessage error={errors.serviceTypes as string} />
        )}
        <div className="flex flex-row items-center gap-2 w-full justify-start">
          {renderServiceTypes()}
        </div>
      </div>
      <DividerLine height="1px" margin="5px 0" />
      <div className="flex flex-col gap-2 w-full">
        {pricingAgreement?.tiers.map((tier, index) => renderTier(tier, index))}
      </div>
      <DividerLine height="1px" margin="5px 0" />
      <div className="flex flex-row items-center justify-between">
        <button
          style={{ border: `1px solid ${colors.gray200}` }}
          className="px-2 py-1 rounded-md flex flex-row items-center"
          onClick={onAddTier}
        >
          <FiPlus size={20} className="mr-2" />
          Add Tier
        </button>
        <div className="flex flex-row items-center gap-2">
          <button
            style={{ border: `1px solid ${colors.gray200}` }}
            className="px-2 py-1 rounded-md flex flex-row items-center"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className={`px-2 py-1 rounded-md flex flex-row items-center text-white bg-green-400 ${
              !isValid ? 'opacity-50' : ''
            }`}
            style={{ cursor: isValid ? 'pointer' : 'not-allowed' }}
            onClick={onSave}
            disabled={!isValid}
          >
            Save Tiers
          </button>
        </div>
      </div>
    </div>
  )
}

export default VendorPricingAgreementTiers

const ErrorMessage: React.FC<{ error: string }> = ({ error }) => {
  return (
    <div
      className="p-4 w-full rounded-md flex flex-row items-center gap-2"
      style={{
        border: `1px solid ${colors.gray200}`,
        backgroundColor: colors.greenError,
      }}
    >
      <FiAlertCircle color="red" size={20} />
      <p className="text-red-500 ml-2">{error}</p>
    </div>
  )
}
